import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { COLORS } from "../../../constants";

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function formatISODateToBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

function fileDateSuffix(): string {
  return `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
}

export type RelatorioPedidosBairroFiltroResumo = {
  label: string;
  value: string;
};

export interface RelatorioPedidosBairroExportInput {
  periodoInicioISO: string;
  periodoFimISO: string;
  agruparPor: "bairro" | "cep";
  filtros: RelatorioPedidosBairroFiltroResumo[];
  linhas: Array<{
    lojaNome: string;
    cidade: string | null;
    bairroNome: string | null;
    quantidadeCeps: number | null;
    quantidadePedidos: number;
  }>;
  totalPedidos: number;
  geradoEmLabel: string;
}

type JsPdfWithAutoTable = jsPDF & {
  lastAutoTable?: { finalY: number };
};

export function exportRelatorioPedidosBairroXlsx(
  input: RelatorioPedidosBairroExportInput
): void {
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);
  const porCep = input.agruparPor === "cep";
  const tituloSecao = porCep
    ? "Totais por bairro — CEPs distintos e pedidos"
    : "Totais por loja, cidade e bairro";
  const headerRow = porCep
    ? ["Loja", "Cidade", "Bairro", "CEPs no bairro", "Quantidade de pedidos"]
    : ["Loja", "Cidade", "Bairro", "Quantidade de pedidos"];
  const rows: (string | number)[][] = [
    ["Dom Belloni"],
    [
      porCep
        ? "Relatório — Pedidos por CEP (integração)"
        : "Relatório — Pedidos por bairro (integração)",
    ],
    [],
    ["Período", `${pIni} a ${pFim}`],
    ...input.filtros.map((f) => [f.label, f.value]),
    [],
    [tituloSecao],
    headerRow,
    ...input.linhas.map((r) =>
      porCep
        ? [
            r.lojaNome,
            r.cidade ?? "—",
            r.bairroNome ?? "—",
            r.quantidadeCeps ?? 0,
            r.quantidadePedidos,
          ]
        : [
            r.lojaNome,
            r.cidade ?? "—",
            r.bairroNome ?? "—",
            r.quantidadePedidos,
          ]
    ),
    [],
    [
      "Total de pedidos (período)",
      ...Array(headerRow.length - 2).fill(""),
      input.totalPedidos,
    ],
    [],
    ["Gerado em", input.geradoEmLabel],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = porCep
    ? [{ wch: 24 }, { wch: 18 }, { wch: 40 }, { wch: 14 }, { wch: 12 }]
    : [{ wch: 24 }, { wch: 18 }, { wch: 48 }, { wch: 12 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Relatório");
  XLSX.writeFile(wb, `relatorio-pedidos-bairro-${fileDateSuffix()}.xlsx`);
}

export function exportRelatorioPedidosBairroPdf(
  input: RelatorioPedidosBairroExportInput
): void {
  const porCep = input.agruparPor === "cep";
  const primary = hexToRgb(COLORS.PRIMARY);
  const primaryDark = hexToRgb(COLORS.PRIMARY_DARK);
  const pIni = formatISODateToBR(input.periodoInicioISO);
  const pFim = formatISODateToBR(input.periodoFimISO);

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;

  doc.setFillColor(primary[0], primary[1], primary[2]);
  doc.rect(0, 0, pageW, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(porCep ? "Pedidos por CEP" : "Pedidos por bairro", margin, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Dom Belloni · Pedidos integração", margin, 19);

  autoTable(doc, {
    startY: 32,
    body: [
      ["Período", `${pIni}  até  ${pFim}`],
      ...input.filtros.map((f) => [f.label, f.value]),
    ],
    theme: "plain",
    styles: { fontSize: 9, textColor: [42, 42, 42], cellPadding: 2.5 },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 36 },
      1: { cellWidth: pageW - margin * 2 - 36 },
    },
    margin: { left: margin, right: margin },
  });

  const docExt = doc as JsPdfWithAutoTable;
  const afterMeta = (docExt.lastAutoTable?.finalY ?? 40) + 5;

  const tableWidth = pageW - margin * 2;
  const pedidosW = 18;
  const lojaW = porCep ? 24 : 28;
  const cidadeW = porCep ? 20 : 22;
  const cepsW = porCep ? 16 : 0;
  const bairroW = porCep
    ? tableWidth - lojaW - cidadeW - cepsW - pedidosW
    : tableWidth - lojaW - cidadeW - pedidosW;

  autoTable(doc, {
    startY: afterMeta,
    head: [
      porCep
        ? ["Loja", "Cidade", "Bairro", "CEPs", "Pedidos"]
        : ["Loja", "Cidade", "Bairro", "Pedidos"],
    ],
    body: input.linhas.map((r) =>
      porCep
        ? [
            r.lojaNome,
            r.cidade ?? "—",
            r.bairroNome ?? "—",
            String(r.quantidadeCeps ?? 0),
            String(r.quantidadePedidos),
          ]
        : [
            r.lojaNome,
            r.cidade ?? "—",
            r.bairroNome ?? "—",
            String(r.quantidadePedidos),
          ]
    ),
    foot: [
      porCep
        ? ["Total no período", "", "", "", String(input.totalPedidos)]
        : ["Total no período", "", "", String(input.totalPedidos)],
    ],
    theme: "striped",
    headStyles: {
      fillColor: primary,
      textColor: 255,
      fontStyle: "bold",
      halign: "left",
      valign: "middle",
    },
    footStyles: {
      fillColor: primaryDark,
      textColor: 255,
      fontStyle: "bold",
    },
    columnStyles: porCep
      ? {
          0: { cellWidth: lojaW, halign: "left" },
          1: { cellWidth: cidadeW, halign: "left" },
          2: { cellWidth: bairroW, halign: "left" },
          3: { cellWidth: cepsW, halign: "right" },
          4: { cellWidth: pedidosW, halign: "right" },
        }
      : {
          0: { cellWidth: lojaW, halign: "left" },
          1: { cellWidth: cidadeW, halign: "left" },
          2: { cellWidth: bairroW, halign: "left" },
          3: { cellWidth: pedidosW, halign: "right" },
        },
    bodyStyles: { textColor: [38, 38, 38], lineColor: [230, 230, 230] },
    alternateRowStyles: { fillColor: [255, 252, 251] },
    margin: { left: margin, right: margin, bottom: 16 },
    styles: {
      fontSize: 8,
      cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 2 },
      lineWidth: 0.08,
    },
  });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7.5);
    doc.setTextColor(115, 115, 115);
    doc.setFont("helvetica", "normal");
    doc.text(
      `${input.geradoEmLabel}  ·  Folha ${i} de ${pages}`,
      margin,
      pageH - 6
    );
  }

  doc.save(`relatorio-pedidos-bairro-${fileDateSuffix()}.pdf`);
}
